import { FC, ReactNode, useCallback } from 'react';
import { ErrorBoundary as SentryErrorBoundary, type ErrorBoundaryProps, getClient } from '@sentry/react';
import { ErrorFallback } from './ErrorFallback/ErrorFallback';
import { DsnComponents } from '@sentry/types';
import { TagCleanedData } from '@src/application/modules/tag/shared/tag/context/cleanTagData';
import { SENTRY_TAG_ERROR_BOUNDARY } from '../../../tag/shared/constants';
import { BucketingDTO } from '../../../flagship/type';

const attachmentUrlFromDsn = (dsn: DsnComponents, eventId?: string) => {
  const { host, path, projectId, port, protocol, publicKey } = dsn;
  return `${protocol}://${host}${port !== '' ? `:${port}` : ''}${
    path !== '' ? `/${path}` : ''
  }/api/${projectId}/events/${eventId}/attachments/?sentry_key=${publicKey}&sentry_version=7&sentry_client=custom-javascript`;
};

const attachTagDataToEvent = async (
  eventId: string,
  data: TagCleanedData | BucketingDTO,
  dataKey: string,
  fileName: string
) => {
  try {
    const client = getClient();
    const dsn = client?.getDsn();
    if (dsn) {
      const endpoint = attachmentUrlFromDsn(dsn, eventId);
      const formData = new FormData();
      formData.append(
        dataKey,
        new Blob([JSON.stringify(data)], {
          type: 'application/json',
        }),
        fileName
      );
      fetch(endpoint, {
        method: 'POST',
        body: formData,
      }).catch((ex) => {
        // we have to catch this otherwise it throws an infinite loop in Sentry
        console.error(ex);
      });
    }
  } catch (ex) {
    console.error(ex);
  }
};

const beforeCapture: ErrorBoundaryProps['beforeCapture'] = (scope) => {
  scope.setTag(SENTRY_TAG_ERROR_BOUNDARY, true);
};

type ErrorBoundaryType = {
  children: ReactNode;
  data?: TagCleanedData | BucketingDTO;
  dataKey?: string;
  fileName?: string;
};

export const ErrorBoundary: FC<ErrorBoundaryType> = ({ children, data, dataKey, fileName }) => {
  const onError: ErrorBoundaryProps['onError'] = useCallback((error, componentStack, eventId) => {
    if (data && dataKey && fileName) {
      attachTagDataToEvent(eventId, data, dataKey, fileName);
    }
  }, []);

  return (
    // @ts-ignore - @sentry/react type conflicts with @types/react. Will be solved moving to @types/react@18
    <SentryErrorBoundary
      beforeCapture={beforeCapture}
      onError={onError}
      fallback={(errors) => <ErrorFallback {...errors} />}
    >
      {children}
    </SentryErrorBoundary>
  );
};
